import React, { useEffect, useState, useMemo } from 'react'
import { useClickStore } from '../../store/clickStore';
import { useShallow } from 'zustand/react/shallow';
import SidebarScene from './SidebarScene';
import SummaryChart from './SummaryChart';
import SamplingData from './SamplingData';
import MiningProductionData from './MiningProductionData';
import BlockObjectDetailsView from './BlockObjectDetailsView';
import { getSiteModules } from '../../config/siteConfig';
import DownholeLog, { holeRecordFromDetails } from '../ui/DownholeLog';

function Section({ title, open, onToggle, children }) {
  return (
    <div className="bg-black/40 border border-yellow-600/25 rounded-sm">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-2 py-1.5 text-left hover:bg-yellow-500/5"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-yellow-500/90">{title}</span>
        <span className={`text-[10px] text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && (
        <div className="border-t border-yellow-600/15 px-2 py-2">
          {children}
        </div>
      )}
    </div>
  );
}

const toNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const sanitized = value.replace(/,/g, '').trim();
    if (!sanitized) return null;
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatDisplay = (value) => (value == null || value === '' ? '-' : String(value));

const getDecimalPlacesForLabel = (label) => {
  const key = String(label || '').toLowerCase().replace(/\s+/g, '');
  if (!key) return null;
  if (key.includes('tonnage') || key.includes('tonage')) return 0;
  if (key.includes('content')) return 1;
  if (key.includes('grade')) return 2;
  if (key.includes('volume')) return 0;
  if (key.includes('meter') || key.includes('metre')) return 1;
  return null;
};

const formatNumericByLabel = (value, label) => {
  const numeric = toNumber(value);
  if (numeric == null) return formatDisplay(value);
  const decimals = getDecimalPlacesForLabel(label);
  if (decimals == null) return formatDisplay(value);
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export default function ObjectDetailsPage({ onOpenStats }) {
    const [data, setData] = useState([]);
    const [isOverviewOpen, setIsOverviewOpen] = useState(true);
    const [isAttributesOpen, setIsAttributesOpen] = useState(false);
    const [isSummaryChartOpen, setIsSummaryChartOpen] = useState(true);
    const [isGeologyOpen, setIsGeologyOpen] = useState(false);
    const [isMiningReturnsOpen, setIsMiningReturnsOpen] = useState(false);
    const [isDownholeOpen, setIsDownholeOpen] = useState(true);
    const { openPit, underground } = getSiteModules();

    const { objectDetails, objectDetailsObject } = useClickStore(useShallow((state) => ({
        objectDetails: state.objectDetails,
        objectDetailsObject: state.objectDetailsObject,
    })));

    const clickedHole = holeRecordFromDetails(objectDetails);

    const objectType = useMemo(() => {
        if (!objectDetails) {
            return 'basic';
        }

        const rawType = String(objectDetails.type || objectDetails.objecttype || '').trim().toLowerCase();
        if (openPit && (rawType === 'block' || objectDetails.blockname || objectDetails.BlockName)) {
            return 'block';
        }

        const name = objectDetails.Name || objectDetails.name;
        if (underground && name !== null && name !== undefined && String(name).toLowerCase().includes('stope')) {
            return 'stoping';
        }

        if (!underground || !objectDetails.objecttype) {
            return rawType === 'block' ? 'block' : 'basic';
        }

        const normalizedType = String(objectDetails.objecttype).trim().toLowerCase();
        const validTypes = ['development', 'stoping', 'tramming', 'hoisting'];
        if (validTypes.includes(normalizedType)) {
            return normalizedType;
        }

        return 'basic';
    }, [objectDetails, openPit, underground]);

    const objectName = useMemo(() => {
        if (!objectDetails) return null;
        return objectDetails.blockname
            || objectDetails.BlockName
            || objectDetails.ObjectName
            || objectDetails.Name
            || objectDetails.StopeID
            || objectDetails.id
            || null;
    }, [objectDetails]);

    const blockId = objectDetails?.id != null ? String(objectDetails.id).trim() : '';

    useEffect(() => {
        const detailsArr = [];
        if (objectDetails) {
            Object.entries(objectDetails).forEach(([key, value]) => {
                detailsArr.push({ name: key, value: value });
            });
        }
        setData(detailsArr);
    }, [objectDetails]);

    useEffect(() => {
        setIsOverviewOpen(true);
        setIsAttributesOpen(false);
        setIsSummaryChartOpen(objectType !== 'basic' && objectType !== 'block' && !!objectName);
        setIsGeologyOpen(objectType !== 'basic' && objectType !== 'block' && !!objectName);
        setIsMiningReturnsOpen(objectType === 'stoping' && !!objectName);
    }, [objectType, objectName]);

    const isBlock = objectType === 'block';
    const shouldShowProductionSections = underground && objectType !== 'basic' && objectType !== 'block' && objectName !== null;
    const isDevelopment = objectType === 'development';
    const isStoping = objectType === 'stoping';
    const canOpenStats = (isBlock && openPit) || shouldShowProductionSections;

    const statisticsTitle = isDevelopment
        ? 'Development Statistics'
        : isStoping
            ? 'Stope Statistics'
            : objectType === 'tramming'
                ? 'Tramming Statistics'
                : objectType === 'hoisting'
                    ? 'Hoisting Statistics'
                    : 'Statistics';

    const samplingTitle = isDevelopment
        ? 'Development Sampling'
        : isStoping
            ? 'Stoping Sampling'
            : 'Sampling Data';

    return (
        <div className="flex flex-col w-full h-full bg-transparent p-1.5 sm:p-2 overflow-y-auto gap-1.5">
            <div className="flex items-baseline justify-between gap-2 px-0.5">
                <h2 className="text-xs font-semibold text-yellow-400 truncate uppercase tracking-[0.1em]">
                    {objectName || 'Object details'}
                </h2>
                <div className="flex items-center gap-1.5 shrink-0">
                    {objectType !== 'basic' && (
                        <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-medium">
                            {objectType}
                        </span>
                    )}
                    {canOpenStats && typeof onOpenStats === 'function' && (
                        <button
                            type="button"
                            className="m-0 h-5 px-1.5 rounded-sm border border-yellow-600/35 text-[8px] uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10"
                            onClick={() => onOpenStats(objectName)}
                            title="Open Stats tab for this object"
                        >
                            Stats
                        </button>
                    )}
                </div>
            </div>

            <Section
                title="Overview"
                open={isOverviewOpen}
                onToggle={() => setIsOverviewOpen(!isOverviewOpen)}
            >
                <div className="h-48 sm:h-56 w-full rounded overflow-hidden bg-zinc-950">
                    <SidebarScene objectDetailsObject={objectDetailsObject} />
                </div>
            </Section>

            {clickedHole.holeId ? (
                <Section
                    title="Downhole log"
                    open={isDownholeOpen}
                    onToggle={() => setIsDownholeOpen(!isDownholeOpen)}
                >
                    <DownholeLog details={objectDetails} hideTitle />
                </Section>
            ) : null}

            {isBlock && openPit && (
                <div className="bg-white text-gray-900 rounded-sm overflow-auto max-h-[28rem] border border-yellow-600/20">
                    <BlockObjectDetailsView
                        blockId={blockId}
                        formatNumericByLabel={formatNumericByLabel}
                    />
                </div>
            )}

            <Section
                title="Attributes"
                open={isAttributesOpen}
                onToggle={() => setIsAttributesOpen(!isAttributesOpen)}
            >
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full table-auto text-xs">
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-zinc-900/50' : 'bg-transparent'}>
                                    <td className="border-b border-zinc-800 px-1.5 py-0.5 font-medium text-zinc-500 whitespace-nowrap">{item.name}</td>
                                    <td className="border-b border-zinc-800 px-1.5 py-0.5 text-zinc-200 break-all">{formatNumericByLabel(item.value, item.name)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {shouldShowProductionSections && (
                <Section
                    title={statisticsTitle}
                    open={isSummaryChartOpen}
                    onToggle={() => setIsSummaryChartOpen(!isSummaryChartOpen)}
                >
                    <SummaryChart
                        objectName={objectName}
                        objectType={objectType}
                        summary_field="Month"
                    />
                </Section>
            )}

            {shouldShowProductionSections && (
                <Section
                    title={samplingTitle}
                    open={isGeologyOpen}
                    onToggle={() => setIsGeologyOpen(!isGeologyOpen)}
                >
                    <SamplingData
                        objectName={objectName}
                        objectType={objectType}
                    />
                </Section>
            )}

            {shouldShowProductionSections && isStoping && (
                <Section
                    title="Mining Production Data"
                    open={isMiningReturnsOpen}
                    onToggle={() => setIsMiningReturnsOpen(!isMiningReturnsOpen)}
                >
                    <MiningProductionData
                        objectName={objectName}
                        objectType={objectType}
                    />
                </Section>
            )}
        </div>
    );
}
